/** A collection of helpers for formatting and assessing wedding dates for inquiries or first moves */

import {
  ExactDateView,
  ExpandedWeddingDatesView,
  ExpandedWeddingDatesRequest,
} from '@zola/svc-marketplace-ts-types';

import _uniq from 'lodash/uniq';

import { MonthsByNumber, WeekdayMeta } from '~/components/common/form/dates';
import { DateValue, parseDate } from '~/util/dateUtils';
import { formatEventDateShort, formatEventMonthYear } from '~/util/formatters';
import TextUtils from '~/util/textUtils';
import { zeroPad } from '~/util/zeroPad';

export type WeddingDateView = ExpandedWeddingDatesView | ExpandedWeddingDatesRequest;
export type InquiryLike = {
  weddingDates: WeddingDateView | null;
  weddingDate: DateValue | null;
  weddingYearMonthDate: DateValue | null;
};

/**
 * Determine if the inquiry-like object has expanded wedding dates: either multiple dates, or flexible dates
 *
 * Note: If they picked one exact date, that is _not_ an expanded date: thats just a single date
 */
export const hasExpandedDates = (inquiry: InquiryLike | null): boolean => {
  return Boolean(inquiry?.weddingDates) && inquiry?.weddingDates?.exactDates?.length !== 1;
};

export const hasFlexibleDates = (weddingDates: WeddingDateView | null): boolean => {
  if (weddingDates) {
    const { flexibleDates } = weddingDates;
    return flexibleDates && flexibleDates.length > 0;
  }
  return false;
};

/**
 * Returns true if the couples HAS MORE THAN ONE EXACT DATE.
 *
 * @param weddingDates
 * @returns
 */
export const hasExactDates = (weddingDates: WeddingDateView | null, numDates = 2): boolean => {
  if (weddingDates) {
    const { exactDates } = weddingDates;

    return exactDates && exactDates.length >= numDates;
  }
  return false;
};

/**
 * Gets the long names of the days of the week requested in an expanded wedding date inquiry
 *
 * @return ['Monday', 'Friday', 'Saturday', 'Sunday]
 * @return ['Saturday', 'Sunday']
 * @return ['Wednesday']
 */
export const getSelectedWeekdays = (weddingDates: WeddingDateView | null): string[] => {
  if (!weddingDates) {
    return [];
  }

  const { daysOfWeek } = weddingDates;

  const sortedDays = [...daysOfWeek].sort((a, b) => WeekdayMeta[a].order - WeekdayMeta[b].order);

  return _uniq(sortedDays.map((d) => WeekdayMeta[d].label));
};

/**
 * Gets the long names of the days of the week the couple requested joined together in a
 * oxford comma style list.
 *
 * @param an inquiry like object
 *
 * @example getFormattedWeekdays(null) => ''
 * @example getFormattedWeekdays(inquiry) => 'Saturday'
 * @example getFormattedWeekdays(inquiry) => 'Saturday or Sunday'
 * @example getFormattedWeekdays(inquiry) => 'Friday, Saturday, or Sunday'
 * @example getFormattedWeekdays(inquiry) => 'Monday, Friday, Saturday, or Sunday'
 * @example getFormattedWeekdays(inquiry) => 'Any day'
 */
export const getFormattedWeekdays = (weddingDates: WeddingDateView | null) => {
  const days = getSelectedWeekdays(weddingDates);
  if (days.length === 7) {
    return 'Any day';
  }
  return TextUtils.formatArrayWithOxfordComma(days, 'or');
};

export const getSelectedYears = (weddingDates: WeddingDateView | null) => {
  if (!weddingDates) {
    return [];
  }

  const { flexibleDates } = weddingDates;

  return flexibleDates
    .map((fd) => (fd.months.length > 0 ? fd.year : undefined)) // throw out a year without a month pick
    .filter(Boolean)
    .sort((a, b) => (a || 0) - (b || 0)) as number[];
};

/**
 *
 * @param inquiry
 * @param year
 * @returns ['January', 'February']
 */
export const getSelectedMonthsForYear = (weddingDates: WeddingDateView | null, year: number) => {
  if (!weddingDates) {
    return [];
  }

  const { flexibleDates } = weddingDates;

  const selectedYear = flexibleDates.find((flexDate) => flexDate.year === year);
  if (!selectedYear) {
    return [];
  }

  return _uniq(
    [...selectedYear.months]
      .sort((a, b) => a - b)
      .map((m) => {
        return MonthsByNumber[m]?.label;
      })
  );
};

export const getFormattedMonthsForYear = (weddingDates: WeddingDateView | null, year: number) => {
  const months = getSelectedMonthsForYear(weddingDates, year);
  if (months.length === 12) {
    return 'Any month';
  }
  return TextUtils.formatArrayWithOxfordComma(months, 'or');
};

export const formattedExpandedDate = (weddingDates: WeddingDateView | null): string => {
  if (hasFlexibleDates(weddingDates)) {
    const years = getSelectedYears(weddingDates).join(', ');
    return `Flexible dates in ${years}`;
  }
  if (hasExactDates(weddingDates)) {
    const exactDates = getExactDates(weddingDates);
    return getFormattedFirstChoiceDateAndNumberOfAlternateDates(exactDates);
  }
  return '';
};

type GetExactDateResult = ExactDateView & {
  date: Date;
};

export const getExactDates = (weddingDates: WeddingDateView | null): GetExactDateResult[] => {
  if (!weddingDates) {
    return [];
  }

  const { exactDates } = weddingDates;

  // Sort in date order, not preferred => rest
  return exactDates
    .map((d) => ({
      ...d,
      date: parseDate(`${d.year}-${zeroPad(d.month)}-${zeroPad(d.day)}`, 'YYYY-MM-DD'),
    }))
    .sort((a, b) => a.date.valueOf() - b.date.valueOf());
};

export const getFormattedFirstChoiceDateAndNumberOfAlternateDates = (
  exactDates: GetExactDateResult[]
) => {
  const preferredDate = getPreferredDate(exactDates);
  const formattedFirstChoiceDate = preferredDate ? formatEventDateShort(preferredDate.date) : null;
  const hasAlternateDates = exactDates.length > 1;
  const alternateDateText = hasAlternateDates ? `+ ${exactDates.length - 1} more` : ``;
  return `${formattedFirstChoiceDate} ${alternateDateText}`;
};

export const getPreferredDate = (exactDates?: GetExactDateResult[]) => {
  return exactDates && exactDates?.length > 0 ? exactDates.find((date) => date.preferred) : null;
};

/**
 * Formats the wedding date from an inquiry like object as a string.
 */
export const formatInquiryWeddingDate = (props: InquiryLike): string | null => {
  const hasMoreThanZeroExactDates = (props.weddingDates?.exactDates?.length || 0) > 1;
  if (
    hasExpandedDates(props) &&
    (hasFlexibleDates(props.weddingDates) || hasMoreThanZeroExactDates)
  ) {
    return formattedExpandedDate(props.weddingDates);
  }
  if (props.weddingDate) {
    return formatEventDateShort(props.weddingDate) || '';
  }
  if (props.weddingYearMonthDate) {
    return formatEventMonthYear(props.weddingYearMonthDate) || '';
  }
  return null;
};
